import { useState } from 'react';
import { fmtLocalDate, fmtLocalDateTime, toWire } from '../../utils/datetime';
import { ComboSelect } from '../ComboSelect';

/**
 * Shared click-to-edit grid cell — BO UI Standard v2 §9 "Editable grids — pure
 * grid, in-place editing" (ruling 2026-07-02).
 *
 * **No textbox drawn at rest**: the cell renders as plain text; the editor
 * appears only WHILE editing (on `trigger`: click or double-click) and commits
 * on blur / Enter (`onCommit(newValue)`); Escape reverts.  The editor type
 * matches the data:
 *   • `text`     — free text in place.
 *   • `combo`    — native select over `options` (pick-from-list, e.g. CollectedBy).
 *   • `datetime` — calendar-with-time (`<input type="datetime-local">`); the
 *     resting cell displays via the shared `fmtLocalDateTime` (MM/DD/YYYY HH:mm,
 *     24-h) and the commit value is produced by the shared `toWire` convention
 *     (§14b.6 — never inline date code).
 *   • `date`     — calendar, date-only (`<input type="date">`); the resting cell
 *     displays via the shared `fmtLocalDate` (MM/DD/YYYY) and commits the same
 *     `toWire` string at LOCAL midnight (§14b.6).
 *
 * Required-yet-empty support: `requiredEmpty` adds `req-empty-cell` (yellow
 * tint `--ai-required-bg`) on the resting cell — pair with a red `*`
 * (`.req-star`) on the column header.  Styles live in `theme/boStandard.css`.
 */
export type InlineCellEditorProps = {
  /** Current raw value.  For `datetime` this is the WIRE string (ISO-shaped). */
  value: string | null | undefined;
  /** Editor kind — defaults to `text`. */
  type?: 'text' | 'combo' | 'datetime' | 'date';
  /** Options for the `combo` editor. */
  options?: string[];
  /** What starts editing — defaults to `click`. */
  trigger?: 'click' | 'double-click';
  /** Called with the new value when the edit commits (blur / Enter) and the
   *  value actually changed.  For `datetime` the value is the `toWire` string. */
  onCommit: (newValue: string) => void;
  /** Optional: fires on every keystroke WHILE still editing (text/combo only —
   *  does not close the cell), so a caller can stage the value into a batch-edit
   *  buffer live rather than waiting for blur/Enter. `onCommit` still fires as
   *  usual when editing finishes; calling both is harmless (same idempotent
   *  staging logic on the caller's end). */
  onLiveChange?: (value: string) => void;
  /** Yellow required-yet-empty tint on the resting cell (§9). */
  requiredEmpty?: boolean;
  /** Read-only cell — renders text only, never opens an editor. */
  disabled?: boolean;
};

/** Wire ISO → the `datetime-local` input value (`YYYY-MM-DDTHH:mm`). */
function wireToInput(wire: string | null | undefined): string {
  if (!wire) return '';
  const m = /^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/.exec(wire);
  return m ? `${m[1]}T${m[2]}` : '';
}

/** Wire ISO → the `date` input value (`YYYY-MM-DD`). */
function wireToDateInput(wire: string | null | undefined): string {
  if (!wire) return '';
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(wire);
  return m ? m[1]! : '';
}

/**
 * Move the edit cursor to the neighbouring editable cell — arrow keys and Tab
 * (user request 2026-08-01: "when I edit the Numeric result or any other edit
 * cell use up/down/side arrow the cursor move the position of the next cell").
 *
 * Walks the real table DOM from the cell being edited, skipping cells that
 * aren't editable (display-only columns, `disabled` editors), and OPENS the
 * target by clicking its resting `.gedit-cell` — the same path a mouse click
 * takes, so no editor state is duplicated. Coordinates are captured
 * synchronously (before the commit re-renders the row) and resolved on the next
 * tick against the live nodes.
 */
function moveCursor(from: HTMLElement, dRow: number, dCol: number): void {
  const td = from.closest('td');
  const tr = td?.parentElement ?? null;
  const tbody = tr?.parentElement ?? null;
  if (!td || !tr || !tbody) return;
  const startCol = Array.prototype.indexOf.call(tr.children, td);
  const startRow = Array.prototype.indexOf.call(tbody.children, tr);
  if (startCol < 0 || startRow < 0) return;

  setTimeout(() => {
    let r = startRow;
    let c = startCol;
    // Bounded walk — one step per iteration in the requested direction.
    for (let guard = 0; guard < 500; guard++) {
      r += dRow;
      c += dCol;
      const row = tbody.children[r] as HTMLElement | undefined;
      if (!row) return;                                   // past the first/last row
      if (c < 0 || c >= row.children.length) return;      // past the first/last column
      const cell = row.children[c]?.querySelector<HTMLElement>('.gedit-cell:not(.disabled)');
      if (cell) {
        // Open it exactly as a pointer would. A `double-click`-trigger cell
        // ignores the click, so follow with a dblclick — the other kind ignores
        // that one in turn, so one of the two always lands.
        cell.click();
        cell.dispatchEvent(new MouseEvent('dblclick', { bubbles: true }));
        return;
      }
    }
  }, 0);
}

export function InlineCellEditor({
  value, type = 'text', options = [], trigger = 'click', onCommit, onLiveChange, requiredEmpty, disabled,
}: InlineCellEditorProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  const restText =
    type === 'datetime' ? fmtLocalDateTime(value)
    : type === 'date'   ? fmtLocalDate(value)
    : (value ?? '');

  const begin = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (disabled) return;
    setDraft(
      type === 'datetime' ? wireToInput(value)
      : type === 'date'   ? wireToDateInput(value)
      : (value ?? ''));
    setEditing(true);
  };

  const commit = (raw: string) => {
    setEditing(false);
    if (type === 'datetime') {
      if (!raw) return;                                  // cleared / untouched — keep as-is
      const d = new Date(raw);
      if (Number.isNaN(d.getTime())) return;
      const wire = toWire(d);
      if (wire !== (value ?? '')) onCommit(wire);
      return;
    }
    if (type === 'date') {
      if (!raw) return;                                  // cleared / untouched — keep as-is
      const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
      if (!m) return;
      // Build LOCAL midnight from the parts — never `new Date('YYYY-MM-DD')`,
      // which parses as UTC and lands on the PREVIOUS day west of Greenwich.
      const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
      if (Number.isNaN(d.getTime())) return;
      const wire = toWire(d);
      if (wire !== (value ?? '')) onCommit(wire);
      return;
    }
    if (raw !== (value ?? '')) onCommit(raw);
  };

  // Live equivalent of commit() for date/datetime — emits the parsed WIRE value
  // (not the raw input) so a caller staging on `onLiveChange` gets the SAME value
  // commit produces. Lets a grid enable "Save changes" the moment a date is picked,
  // before blur/Tab (GoLive parity). No-op until the input holds a full valid date.
  const emitLive = (raw: string) => {
    if (!onLiveChange) return;
    if (type === 'datetime') {
      if (!raw) return;
      const d = new Date(raw);
      if (!Number.isNaN(d.getTime())) onLiveChange(toWire(d));
      return;
    }
    if (type === 'date') {
      const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
      if (!m) return;
      const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
      if (!Number.isNaN(d.getTime())) onLiveChange(toWire(d));
      return;
    }
    onLiveChange(raw);
  };

  if (editing) {
    const keys = (e: React.KeyboardEvent<HTMLInputElement | HTMLSelectElement>) => {
      const el = e.currentTarget;
      // Commit what's typed, then open the neighbouring editable cell.
      const step = (dRow: number, dCol: number) => {
        e.preventDefault();
        moveCursor(el, dRow, dCol);   // reads the DOM NOW, clicks after the re-render
        commit(el.value);
      };

      if (e.key === 'Enter') { e.preventDefault(); commit(el.value); return; }
      if (e.key === 'Escape') { e.preventDefault(); setEditing(false); return; }
      // Tab walks to the next / previous editable cell in the row (every editor
      // type) — at rest no textbox exists, so the browser's own tab order has
      // nothing to land on.
      if (e.key === 'Tab') { step(0, e.shiftKey ? -1 : 1); return; }

      // Arrow keys move BETWEEN cells for plain text only. On a combo the arrows
      // pick an option and on date/datetime they step the focused segment, so
      // hijacking them there would break the editor itself.
      if (type !== 'text') return;
      if (e.key === 'ArrowUp')   { step(-1, 0); return; }
      if (e.key === 'ArrowDown') { step(1, 0); return; }
      // Left / right only once the caret has run off the end of the text, so
      // arrowing THROUGH what you typed still works normally.
      const input = el as HTMLInputElement;
      const caret = input.selectionStart;
      const collapsed = caret !== null && caret === input.selectionEnd;
      if (e.key === 'ArrowLeft'  && collapsed && caret === 0) step(0, -1);
      if (e.key === 'ArrowRight' && collapsed && caret === input.value.length) step(0, 1);
    };
    const stop = (e: React.MouseEvent) => e.stopPropagation();
    return (
      <span onClick={stop} onDoubleClick={stop}
        onKeyDown={e => { if (e.key === 'Escape') { e.stopPropagation(); setEditing(false); } }}>
        {type === 'combo' ? (
          <ComboSelect
            value={draft}
            options={options}
            onChange={val => { onLiveChange?.(val); commit(val); }}
            placeholder="N/A"
            className="gedit-combo"
            autoOpen
          />
        ) : (
          <input autoFocus className="gedit-editor"
            type={type === 'datetime' ? 'datetime-local' : type === 'date' ? 'date' : 'text'}
            value={draft}
            onChange={e => {
              setDraft(e.target.value);
              // Plain text stages every keystroke; date/datetime emit the parsed
              // WIRE value once the input is a complete date (emitLive), so a caller
              // can enable "Save changes" the instant a date is picked (GoLive parity).
              if (type !== 'datetime' && type !== 'date') onLiveChange?.(e.target.value);
              else emitLive(e.target.value);
            }}
            onBlur={e => commit(e.target.value)}
            onKeyDown={keys} />
        )}
      </span>
    );
  }

  return (
    <span
      className={`gedit-cell${requiredEmpty ? ' req-empty-cell' : ''}${disabled ? ' disabled' : ''}`}
      title={disabled ? undefined : (trigger === 'click' ? 'Click to edit' : 'Double-click to edit')}
      onClick={trigger === 'click' ? begin : e => e.stopPropagation()}
      onDoubleClick={trigger === 'double-click' ? begin : undefined}>
      {restText}
    </span>
  );
}
